import React, { useEffect, useRef, useState } from 'react';

const NAVY = '#071e62';
const GOLD = '#c9a227';
const STAGES = [
  { until: 30, text: 'Initialisation...' },
  { until: 80, text: 'Chargement des données...' },
  { until: 100, text: 'Finalisation...' },
];
const SEEN_KEY = 'nsia_splash_seen';

/** Vrai a la premiere ouverture de la page de connexion dans cette session du navigateur. */
export const shouldShowSplash = () => {
  try {
    return !sessionStorage.getItem(SEEN_KEY);
  } catch (e) {
    return false;
  }
};

/** Reseau de particules or et bleu, en fond de l'ecran de demarrage. */
const Particles = () => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    const mobile = window.innerWidth < 768;
    const reach = mobile ? 80 : 120;
    let frame;
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    const dots = Array.from({ length: mobile ? 25 : 55 }, (_, i) => ({
      x: Math.random() * canvas.width, y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
      r: Math.random() * 2 + 1, gold: i % 4 === 0,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach((d, i) => {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0 || d.x > canvas.width) d.vx *= -1;
        if (d.y < 0 || d.y > canvas.height) d.vy *= -1;
        for (let j = i + 1; j < dots.length; j += 1) {
          const dist = Math.hypot(d.x - dots[j].x, d.y - dots[j].y);
          if (dist < reach) {
            ctx.strokeStyle = `rgba(90, 150, 240, ${(1 - dist / reach) * 0.25})`;
            ctx.beginPath(); ctx.moveTo(d.x, d.y); ctx.lineTo(dots[j].x, dots[j].y); ctx.stroke();
          }
        }
        ctx.fillStyle = d.gold ? 'rgba(201, 162, 39, 0.8)' : 'rgba(90, 150, 240, 0.6)';
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2); ctx.fill();
      });
      frame = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} aria-hidden="true" style={{ position: 'absolute', inset: 0 }} />;
};

/** Ecran de demarrage NSIA Asset Management, avant la page de connexion. */
const SplashScreen = ({ onDone }) => {
  const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const [progress, setProgress] = useState(0);
  const [leaving, setLeaving] = useState(false);
  // Le parent passe une fonction neuve a chaque rendu : sans ref, l'animation repartirait de zero.
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    const total = reduced ? 600 : 2900; // duree de la version initiale crm-nsia
    const start = performance.now();
    let frame;
    let exit;
    const tick = (now) => {
      const value = Math.min(100, ((now - start) / total) * 100);
      setProgress(value);
      if (value < 100) {
        frame = requestAnimationFrame(tick);
      } else {
        setLeaving(true);
        exit = setTimeout(() => {
          try { sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* navigation privee */ }
          done.current();
        }, 450);
      }
    };
    frame = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(frame); clearTimeout(exit); };
  }, [reduced]);

  const stage = STAGES.find((s) => progress <= s.until) || STAGES[STAGES.length - 1];

  return (
    <div role="status" aria-live="polite" aria-label={`Chargement du CRM NSIA Asset Management : ${Math.round(progress)} %`}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        background: `radial-gradient(circle at 50% 40%, #0d2a80 0%, ${NAVY} 60%, #040f36 100%)`,
        opacity: leaving ? 0 : 1, transition: 'opacity 450ms ease', fontFamily: "'Space Grotesk', sans-serif",
      }}>
      <style>{`
        @keyframes nsiaSplashIn { from { opacity: 0; transform: translateY(14px) scale(.96); } to { opacity: 1; transform: none; } }
        @keyframes nsiaSplashGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(201,162,39,.45), 0 20px 50px rgba(0,0,0,.45); } 50% { box-shadow: 0 0 0 14px rgba(201,162,39,0), 0 20px 50px rgba(0,0,0,.45); } }
        @keyframes nsiaSplashDot { 0%,80%,100% { transform: translateY(0); opacity: .5; } 40% { transform: translateY(-8px); opacity: 1; } }
        @media (prefers-reduced-motion: reduce) { .nsia-splash-anim { animation: none !important; } }
      `}</style>
      {!reduced && <Particles />}

      <div className="nsia-splash-anim" style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 440, padding: '0 24px', textAlign: 'center', animation: 'nsiaSplashIn 700ms ease-out both' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 22, marginBottom: 18 }}>
          <img src="/nsia.png" alt="NSIA Asset Management" className="nsia-splash-anim"
            style={{ width: 'clamp(84px, 22vw, 124px)', aspectRatio: '1', objectFit: 'cover', borderRadius: 26, border: `2px solid ${GOLD}`, animation: 'nsiaSplashGlow 2.2s ease-in-out infinite' }} />
          <span style={{ fontSize: 'clamp(30px, 8vw, 48px)', fontWeight: 800, color: '#fff', letterSpacing: 1 }}>CRM</span>
        </div>
        <p style={{ margin: '0 0 30px', fontSize: 'clamp(15px, 4vw, 20px)', color: 'rgba(220,230,255,.9)' }}>Plateforme de gestion de la clientèle</p>

        <div style={{ height: 7, borderRadius: 99, background: 'rgba(255,255,255,.12)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progress}%`, borderRadius: 99, background: `linear-gradient(90deg, ${GOLD}, #f3d77a, #ffffff)`, boxShadow: `0 0 12px ${GOLD}` }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 13, color: '#fff' }}>
          <span>{stage.text}</span>
          <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{Math.round(progress)}%</span>
        </div>

        <div aria-hidden="true" style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 24 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} className="nsia-splash-anim" style={{ width: 10, height: 10, borderRadius: 99, background: i === 1 ? GOLD : '#fff', animation: `nsiaSplashDot 1.2s ${i * 0.18}s ease-in-out infinite` }} />
          ))}
        </div>
        <p style={{ marginTop: 26, fontSize: 12, color: 'rgba(220,230,255,.65)' }}>Connexion sécurisée • NSIA Asset Management</p>
      </div>
    </div>
  );
};

export default SplashScreen;
